import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Container } from "react-bootstrap";
import { Link } from "react-router-dom";
import api, { mediaUrl } from "../../api/axios";

const notificationFilters = [
  { key: "all", label: "Todas" },
  { key: "unread", label: "No leidas" },
  { key: "trades", label: "Intereses", match: type => type?.startsWith("trade") || type === "card_liked" },
  { key: "matches", label: "Matches", match: type => type?.includes("match") || type?.includes("matching") || type === "community_opportunity" },
  { key: "connections", label: "Conexiones", match: type => type?.includes("connection") || type === "new_follower" },
  { key: "messages", label: "Mensajes", match: type => type === "private_message" },
  { key: "community", label: "Comunidad", match: type => type?.includes("business") || type?.includes("community") },
  { key: "products", label: "Productos", match: type => type?.includes("product") },
  { key: "events", label: "Eventos", match: type => type?.includes("tournament") || type?.includes("event") || type?.includes("trade_day") }
];

const internalNotificationPath = value => {
  if (!value) return "";
  try {
    const parsed = new URL(value, window.location.origin);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return value.startsWith("/") ? value : "";
  }
};

const matchDetailPath = notification => {
  const metadata = notification.metadata || {};
  if (notification.type === "card_match_found" && metadata.matchedUserId) {
    const params = new URLSearchParams({ tab: "binder" });
    if (metadata.matchedUserCardId) params.set("card", metadata.matchedUserCardId);
    return `/u/${metadata.matchedUserId}?${params.toString()}`;
  }
  if (notification.type === "card_wanted_match" && metadata.seekerUserId) {
    const params = new URLSearchParams({ tab: "wanted" });
    if (metadata.wantedUserCardId && !String(metadata.wantedUserCardId).startsWith("set-missing:")) {
      params.set("card", metadata.wantedUserCardId);
    }
    return `/u/${metadata.seekerUserId}?${params.toString()}`;
  }
  return internalNotificationPath(metadata.url);
};

function NotificationsHome() {
  const [notifications, setNotifications] = useState([]);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [activeFilter, setActiveFilter] = useState(() => new URLSearchParams(window.location.search).get("filter") || "all");
  const [actionBusy, setActionBusy] = useState("");
  const [startedMatches, setStartedMatches] = useState([]);

  const loadNotifications = useCallback(() => {
    setError("");
    api.get("/notifications").then(({ data }) => setNotifications(data)).catch(() => setError("No pudimos cargar las notificaciones."));
  }, []);

  useEffect(() => {
    loadNotifications();
    const timer = setInterval(loadNotifications, 20000);
    return () => clearInterval(timer);
  }, [loadNotifications]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(""), 4200);
    return () => clearTimeout(timer);
  }, [notice]);

  const markRead = async notificationId => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      await loadNotifications();
    } catch (requestError) {
      setError("No pudimos marcar la notificacion como leida.");
    }
  };

  const markAllRead = async () => {
    try {
      await api.patch("/notifications/read-all");
      setNotice("Notificaciones marcadas como leidas.");
      await loadNotifications();
    } catch (requestError) {
      setError("No pudimos marcar todas las notificaciones como leidas.");
    }
  };

  const answerConnection = async (notification, action) => {
    const requestId = notification.metadata?.connectionRequestId;
    if (!requestId) return;
    await api.post(`/social/connection-requests/${requestId}/${action}`);
    setNotice(action === "accept" ? "Conexion aceptada." : "Solicitud rechazada.");
    await markRead(notification.id);
  };

  const proposeMatchTrade = async notification => {
    const receiverId = notification.metadata?.matchedUserId || notification.metadata?.receiverId;
    const requestedCardId = notification.metadata?.matchedUserCardId;
    if (!receiverId || !requestedCardId) return setError("Este match no tiene suficiente informacion para iniciar el intercambio.");
    setActionBusy(notification.id); setError("");
    try {
      await api.post("/trades", { receiverId, requestedUserCardIds: [requestedCardId], offeredUserCardIds: [], message: "Hola, Cardora encontro un match con " + (notification.metadata?.cardName || "esta carta") + ". Me interesa intercambiarla. Hablamos?" });
      setStartedMatches(current => [...current, notification.id]);
      setNotice("Interes enviado. Ya aparece en Intereses y la otra persona recibira un correo.");
      await markRead(notification.id);
    } catch (requestError) { setError(requestError?.response?.data?.error || "No pudimos iniciar el intercambio."); }
    finally { setActionBusy(""); }
  };

  const offerMatchCard = async notification => {
    const receiverId = notification.metadata?.seekerUserId;
    if (!receiverId) return setError("No encontramos al coleccionista que busca la carta.");
    setActionBusy(notification.id); setError("");
    try {
      await api.post("/trades", { receiverId, requestedUserCardIds: [], offeredUserCardIds: [notification.metadata?.matchedUserCardId], message: "Hola, Cardora me aviso que buscas " + (notification.metadata?.cardName || "esta carta") + ". La tengo disponible. Coordinamos?" });
      setStartedMatches(current => [...current, notification.id]);
      setNotice("Interes enviado. Ya aparece en Intereses y la otra persona recibira un correo.");
      await markRead(notification.id);
    } catch (requestError) { setError(requestError?.response?.data?.error || "No pudimos iniciar el interes."); }
    finally { setActionBusy(""); }
  };

  const unreadCount = notifications.filter(notification => !notification.read_at).length;
  const filteredNotifications = useMemo(() => {
    const filter = notificationFilters.find(item => item.key === activeFilter);
    if (!filter || filter.key === "all") return notifications;
    if (filter.key === "unread") return notifications.filter(notification => !notification.read_at);
    return notifications.filter(notification => filter.match?.(notification.type));
  }, [activeFilter, notifications]);

  return (
    <Container className="page-band feed-page">
      <div className="notification-page-header">
        <div>
          <h1 className="section-title mb-1">Notificaciones</h1>
          <p className="muted-copy mb-0">Matches, intereses, mensajes y actividad importante de tus comunidades.</p>
        </div>
        <div className="notification-header-actions">
          {unreadCount > 0 && <span className="notification-count-pill">{unreadCount} nuevas</span>}
          {unreadCount > 0 && <Button size="sm" variant="outline-dark" onClick={markAllRead}>Marcar leidas</Button>}
        </div>
      </div>

      <div className="notification-filter-row mb-3">
        {notificationFilters.map(filter => (
          <button
            type="button"
            className={`notification-filter ${activeFilter === filter.key ? "is-active" : ""}`}
            onClick={() => setActiveFilter(filter.key)}
            key={filter.key}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {notice && <div className="inline-page-notice mb-3">{notice}</div>}
      {error && <div className="inline-page-notice mb-3">{error}</div>}
      <div className="notification-list">
        {filteredNotifications.length === 0 && (
          <Card className="cardora-card">
            <Card.Body>No hay notificaciones en este filtro.</Card.Body>
          </Card>
        )}
        {filteredNotifications.map(notification => (
          <Card className={`cardora-card notification-card ${notification.read_at ? "is-read" : "is-unread"}`} key={notification.id} onClick={() => markRead(notification.id)}>
            <Card.Body>
              <div className="d-flex justify-content-between gap-3">
                <div className="notification-card-main">
                  <div className="notification-card-title">
                    <span className="notification-icon"><i className="bi bi-bell-fill" /></span>
                    {notification.actor_id ? (
                      <Link
                        to={`/u/${notification.actor_handle || notification.actor_id}`}
                        className="notification-actor-link"
                        onClick={event => event.stopPropagation()}
                      >
                        {notification.actor_name || "Coleccionista"}
                      </Link>
                    ) : <strong>Cardora</strong>}
                  </div>
                  <div className="notification-body">{notification.body}</div>
                  {notification.type?.includes("match") && notification.metadata?.cardName && (
                    <div className="notification-card-preview">
                      {notification.metadata?.cardImageUrl && <img src={mediaUrl(notification.metadata.cardImageUrl)} alt={notification.metadata.cardName} />}
                      <div><strong>{notification.metadata.cardName}</strong><span>{notification.type === "card_match_found" ? "Disponible para intercambio" : "Hay un coleccionista buscandola"}</span></div>
                    </div>
                  )}
                  {notification.type === "card_liked" && (
                    <div className="notification-card-preview">
                      {notification.metadata?.cardImageUrl && (
                        <img src={mediaUrl(notification.metadata.cardImageUrl)} alt={notification.metadata?.cardName || "Carta"} />
                      )}
                      <div>
                        <strong>{notification.metadata?.cardName || "Carta"}</strong>
                        <span>{notification.metadata?.description || [notification.metadata?.setName, notification.metadata?.collectorNumber, notification.metadata?.rarity].filter(Boolean).join(" Â· ") || "Carta de tu binder"}</span>
                      </div>
                    </div>
                  )}
                  <div className="notification-actions">
                    {["card_match_found", "card_wanted_match"].includes(notification.type) && matchDetailPath(notification) && (
                      <Link to={matchDetailPath(notification)} className="notification-action-link" onClick={event => event.stopPropagation()}>Ver detalle</Link>
                    )}
                    {notification.type === "card_match_found" && notification.metadata?.matchedUserCardId && (startedMatches.includes(notification.id) ? <Link to="/trades" className="notification-action-link">Ver en Intereses</Link> : <Button size="sm" className="btn-cardora" disabled={actionBusy === notification.id} onClick={event => { event.stopPropagation(); proposeMatchTrade(notification); }}>{actionBusy === notification.id ? "Enviando..." : "Me interesa"}</Button>)}
                    {notification.type === "card_wanted_match" && notification.metadata?.seekerUserId && (startedMatches.includes(notification.id) ? <Link to="/trades" className="notification-action-link">Ver en Intereses</Link> : <Button size="sm" variant="outline-dark" disabled={actionBusy === notification.id} onClick={event => { event.stopPropagation(); offerMatchCard(notification); }}>{actionBusy === notification.id ? "Enviando..." : "La tengo, coordinemos"}</Button>)}
                    {notification.metadata?.url && !["card_match_found", "card_wanted_match"].includes(notification.type) && <Link to={internalNotificationPath(notification.metadata.url)} className="notification-action-link" onClick={event => event.stopPropagation()}>{notification.type === "community_opportunity" ? "Ver oportunidad" : "Ver detalle"}</Link>}
                    {notification.type?.startsWith("trade") && !notification.type?.includes("day_match") && <Link to="/trades" className="notification-action-link">Ver interes</Link>}
                    {notification.type?.includes("trade_day_match") && notification.metadata?.businessSlug && <Link to={`/store/${notification.metadata.businessSlug}`} className="notification-action-link">Ver evento</Link>}
                    {notification.type === "private_message" && <Link to="/messages" className="notification-action-link">Abrir mensajes</Link>}
                    {notification.metadata?.matchId && <Link to={`/tournaments/${notification.metadata.tournamentId}`} className="notification-action-link">Revisar resultado</Link>}
                    {notification.metadata?.receiverId && <Link to={`/u/${notification.metadata.receiverId}`} className="notification-action-link">Ver perfil</Link>}
                    {notification.metadata?.tournamentId && <Link to={`/tournaments/${notification.metadata.tournamentId}`} className="notification-action-link">Ver evento</Link>}
                    {notification.metadata?.businessSlug && <Link to={`/store/${notification.metadata.businessSlug}`} className="notification-action-link">Ver tienda</Link>}
                  </div>
                  {notification.type === "connection_request" && !notification.read_at && (
                    <div className="notification-actions mt-2">
                      <Button size="sm" className="btn-cardora" onClick={event => {
                        event.stopPropagation();
                        answerConnection(notification, "accept");
                      }}>
                        Aceptar
                      </Button>
                      <Button size="sm" variant="outline-secondary" onClick={event => {
                        event.stopPropagation();
                        answerConnection(notification, "decline");
                      }}>
                        Rechazar
                      </Button>
                    </div>
                  )}
                  <div className="muted-copy small mt-2">{new Date(notification.created_at).toLocaleString()}</div>
                </div>
                {!notification.read_at && <Badge className="notification-new-pill">Nuevo</Badge>}
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>
    </Container>
  );
}

export default NotificationsHome;
